import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from '../../../components/ui';
import { X, Search, AlertTriangle } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { formatTimeCR } from '../../../utils/date';
import { formatTicketForPrint } from '../../../utils/ticketFormat';
import { printAnulacionByDestination, type AnulacionPayload } from '../../../utils/printer';

interface TicketResumen {
  id: string;
  total: number;
  metodo_pago: string | null;
  estado: string;
  created_at: string;
  ticket_number?: string | null;
  jugadas: Array<{ num1: string; num2?: string; monto: number; estado: string }>;
}

interface AnularTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  sorteoId: string;
  usuarioId: string;
  sorteoEstado?: string;
}

const buscarEnTicket = (ticket: TicketResumen, query: string) => {
  const idLower = ticket.id.toLowerCase();
  const largoLower = (ticket.ticket_number || '').toLowerCase();
  const cortoLower = ticket.ticket_number
    ? formatTicketForPrint(ticket.ticket_number).toLowerCase()
    : '';
  return (
    idLower.includes(query) ||
    (largoLower !== '' && largoLower.includes(query)) ||
    (cortoLower !== '' && cortoLower.includes(query))
  );
};

export const AnularTicketModal: React.FC<AnularTicketModalProps> = ({
  isOpen,
  onClose,
  sorteoId,
  usuarioId,
}) => {
  const [tickets, setTickets] = useState<TicketResumen[]>([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [anulando, setAnulando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exito, setExito] = useState<string | null>(null);
  const [ticketBuscado, setTicketBuscado] = useState<TicketResumen | null>(null);
  const [buscandoRemoto, setBuscandoRemoto] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cargar solo tickets anulables al abrir
  useEffect(() => {
    if (!isOpen || !sorteoId) return;
    setError(null);
    setExito(null);
    setConfirmId(null);
    setBusqueda('');
    setTicketBuscado(null);
    cargarTickets();
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen, sorteoId]);

  const cargarTickets = async () => {
    setLoading(true);
    try {
      // solo_anulables=true: backend filtra por estado ABIERTO,
      // sorteo no cerrado, dentro de hora_cierre, sin jugadas en lista
      const data = await fetchAPI<TicketResumen[]>(
        `/tickets/ultimos?sorteo_id=${encodeURIComponent(sorteoId)}&limit=10&solo_anulables=true`
      );
      setTickets(data);
    } catch {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  // La búsqueda empieza local sobre los 10 anulables recientes. Si no hay match
  // suficiente, delega en el endpoint anulable que ya valida permisos y reglas.
  const busquedaTrim = busqueda.trim();
  const ticketsLocalesFiltrados = busquedaTrim
    ? tickets.filter(t => buscarEnTicket(t, busquedaTrim.toLowerCase()))
    : tickets;
  const mostrandoTicketBuscado = Boolean(busquedaTrim && ticketsLocalesFiltrados.length === 0 && ticketBuscado);
  const ticketsFiltrados = mostrandoTicketBuscado && ticketBuscado
    ? [ticketBuscado]
    : ticketsLocalesFiltrados;

  useEffect(() => {
    const query = busqueda.trim();
    const hayResultadoLocal = query
      ? tickets.some(t => buscarEnTicket(t, query.toLowerCase()))
      : true;

    setTicketBuscado(null);
    setConfirmId(null);

    if (!isOpen || !sorteoId || query.length < 4 || hayResultadoLocal) {
      setBuscandoRemoto(false);
      return;
    }

    let cancelado = false;
    setBuscandoRemoto(true);
    const timeoutId = window.setTimeout(async () => {
      try {
        const data = await fetchAPI<TicketResumen>(`/tickets/${encodeURIComponent(query)}/anulable`);
        if (!cancelado) {
          setTicketBuscado(data);
          setError(null);
        }
      } catch (e: any) {
        if (!cancelado) {
          setTicketBuscado(null);
          setError(e.message || 'No se pudo buscar el ticket');
        }
      } finally {
        if (!cancelado) {
          setBuscandoRemoto(false);
        }
      }
    }, 400);

    return () => {
      cancelado = true;
      window.clearTimeout(timeoutId);
    };
  }, [busqueda, tickets, isOpen, sorteoId]);

  const handleAnular = async (ticketId: string) => {
    setAnulando(true);
    setError(null);
    setExito(null);
    try {
      const ticket = tickets.find(t => t.id === ticketId) ??
        (ticketBuscado?.id === ticketId ? ticketBuscado : null);
      const displayName = ticket?.ticket_number
        ? formatTicketForPrint(ticket.ticket_number)
        : ticketId;
      await fetchAPI<any>(`/tickets/${encodeURIComponent(ticketId)}/anular`, {
        method: 'POST',
        body: JSON.stringify({ usuario_id: usuarioId }),
      });

      // Imprimir copia de anulación (nativo, BLE o navegador)
      if (ticket) {
        const now = new Date();
        const fechaAnulacion = `${now.toLocaleDateString('es-CR')} ${now.toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}`;
        const anulacionPayload: AnulacionPayload = {
          ticketCode: ticket.ticket_number
            ? formatTicketForPrint(ticket.ticket_number)
            : ticketId,
          total: ticket.total,
          jugadas: ticket.jugadas?.map(j => ({ num1: j.num1, num2: j.num2, monto: j.monto })),
          fechaAnulacion,
        };
        const printResult = await printAnulacionByDestination(anulacionPayload);
        if (!printResult.ok) {
          console.warn('[AnularTicketModal] Error imprimiendo anulación:', printResult.error);
        }
      }

      setExito(`Ticket ${displayName} anulado correctamente`);
      setConfirmId(null);
      setBusqueda('');
      // Recargar lista
      await cargarTickets();
    } catch (e: any) {
      setError(e.message || 'Error al anular ticket');
      setConfirmId(null);
    } finally {
      setAnulando(false);
    }
  };

  const formatHora = (iso: string) => {
    try {
      return formatTimeCR(iso);
    } catch {
      return '';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Anular Ticket" size="lg">
      <div className="space-y-4">
        {/* Busqueda */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={busqueda}
              onChange={e => {
                setBusqueda(e.target.value);
                setError(null);
                setTicketBuscado(null);
              }}
              placeholder="Buscar por número de ticket..."
              className="w-full pl-9 pr-3 py-2 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none text-sm"
              autoComplete="off"
            />
          </div>
        </div>

        {/* Mensajes */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
            <AlertTriangle size={16} />
            {error}
          </div>
        )}
        {exito && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">
            {exito}
          </div>
        )}

        {/* Lista de tickets del sorteo actual */}
        {(
          <div className="max-h-[50vh] overflow-y-auto space-y-2">
            {loading || buscandoRemoto ? (
              <p className="text-center text-gray-500 py-8 text-sm">
                {buscandoRemoto ? 'Buscando...' : 'Cargando...'}
              </p>
            ) : ticketsFiltrados.length === 0 ? (
              <p className="text-center text-gray-500 py-8 text-sm">
                {busqueda
                  ? 'No se encontraron tickets que coincidan con la búsqueda.'
                  : 'No hay tickets anulables en este sorteo'}
              </p>
            ) : (
              ticketsFiltrados.map(ticket => (
                <div
                  key={ticket.id}
                  className="border rounded-lg p-3 transition-all bg-white border-gray-200 hover:border-gray-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold text-gray-900 truncate">
                          {ticket.ticket_number
                            ? formatTicketForPrint(ticket.ticket_number)
                            : ticket.id}
                        </span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-800">
                          {ticket.estado}
                        </span>
                        {mostrandoTicketBuscado && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800">
                            encontrado por busqueda
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                        <span>{formatHora(ticket.created_at)}</span>
                        <span>{ticket.metodo_pago || '-'}</span>
                        <span>{ticket.jugadas?.length || 0} jugadas</span>
                        <span className="font-mono font-bold text-gray-700">
                          ${typeof ticket.total === 'number' ? ticket.total.toLocaleString() : ticket.total}
                        </span>
                      </div>
                    </div>

                    <div className="flex-shrink-0 ml-2">
                      {confirmId === ticket.id ? (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleAnular(ticket.id)}
                            disabled={anulando}
                            isLoading={anulando}
                          >
                            Confirmar
                          </Button>
                          <button
                            onClick={() => setConfirmId(null)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setConfirmId(ticket.id);
                            setError(null);
                            setExito(null);
                          }}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          Anular
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Boton cerrar */}
        <div className="flex justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AnularTicketModal;
